import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useSetProposalGroupChildSelected from '@/api/mutations/proposals/useSetProposalGroupChildSelected';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import {
  SetProposalGroupChildSelectedInput,
  SetProposalGroupChildSelectedMutation,
} from '@/gql/graphql';

import { applyProposalGroupChildSelection } from './applyProposalGroupChildSelection';
import { createOptimisticProposalMutationOptions } from './createOptimisticProposalMutation';

/**
 * Optimistic wrapper around {@link useSetProposalGroupChildSelected}: applies
 * the selection change to the cached proposal in `onMutate` so the radio /
 * checkbox updates immediately, then rolls back on error. The base hook
 * invalidates the proposal on success, reconciling with server truth.
 */
const useOptimisticSetProposalGroupChildSelected = (args: {
  proposalId: string;
  options?: UseMutationOptions<
    SetProposalGroupChildSelectedMutation,
    unknown,
    SetProposalGroupChildSelectedInput
  >;
}) => {
  const { proposalId, options } = args;
  const queryClient = useQueryClient();
  const queryKey = proposalKeys.proposal(proposalId);

  return useSetProposalGroupChildSelected({
    proposalId,
    options: {
      ...options,
      ...createOptimisticProposalMutationOptions({
        queryClient,
        queryKey,
        apply: applyProposalGroupChildSelection,
        options,
      }),
    },
  });
};

export default useOptimisticSetProposalGroupChildSelected;
